/**
 * Shared timing registry for measuring status change round-trip latency.
 *
 * Flow:
 *  1. changeUnitStatus() records a timestamp when it sends the request
 *  2. TripManager.onTripUpdateMsg() checks if the incoming update matches a pending stamp
 *  3. If matched, emits a LogRocket event with the round-trip duration, then cleans up
 *
 * Entries auto-expire after 30 seconds to prevent memory leaks from lost responses.
 */

import { METRIC_THRESHOLDS } from '@/utils/metricsConstants';

interface PendingStamp {
  startMs: number;
  toStatusId: string;
  toStatusLabel?: string;
}

const pendingStamps = new Map<string, PendingStamp>();
const EXPIRY_MS = METRIC_THRESHOLDS.STATUS_CHANGE_EXPIRY_MS;
const MAX_PENDING = METRIC_THRESHOLDS.STATUS_CHANGE_MAX_PENDING;

/**
 * Record that a status change was sent for a given trip.
 */
export function recordStatusChangeSent(tripId: string, toStatusId: string, toStatusLabel?: string): void {
  //  Safety cap: purge oldest if map grows unexpectedly large
  if (pendingStamps.size >= MAX_PENDING) {
    const [firstKey] = pendingStamps.keys();
    if (firstKey) pendingStamps.delete(firstKey);
  }

  pendingStamps.set(tripId, {
    startMs: performance.now(),
    toStatusId,
    toStatusLabel,
  });

  //  Auto-cleanup after expiry to prevent leaks
  setTimeout(() => {
    pendingStamps.delete(tripId);
  }, EXPIRY_MS);
}

/**
 * Check if there's a pending status change for this trip and return timing info.
 * Returns undefined if no pending stamp exists (e.g., update was server-initiated).
 */
export function consumeStatusChangeTiming(tripId: string): PendingStamp | undefined {
  const pending = pendingStamps.get(tripId);
  if (pending) {
    pendingStamps.delete(tripId);
  }
  return pending;
}
